import {
  ButtonBase,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { EmphasizedNumber, Icons, ItemImageArea } from 'utoniq-ui'

import {
  StoreItemAreaTemporaryFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

type StoreItemAreaTemporaryProps = {
  item: StoreItemAreaTemporaryFragment
  /** analyticsクリックイベント時の導線名 */
  conductorName: string
  responsive?: boolean
}

export const StoreItemAreaTemporary: Organism<StoreItemAreaTemporaryProps> = ({
  item,
  conductorName,
  responsive = false,
}) => {
  const { t } = useTranslation('artistTop')
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()
  const mdUp = useMediaQuery<Theme>(theme => theme.breakpoints.up('md'))

  return (
    <ButtonBase
      sx={{
        width: responsive ? { xs: 80, md: 108 } : 80,
        flexDirection: 'column',
        textAlign: 'left',
      }}
      href={generatePath(routerPages.ArtistStoreItemDetail, {
        artistId: item.issuedArtist?.id,
        itemId: item.id,
      })}
      onClick={() => {
        analytics?.track('click_select', {
          applicationKey: config.applicationKey,
          artistId: item.issuedArtist?.id,
          itemId: item.id,
          name: conductorName,
        })
      }}
    >
      <Stack alignItems="center">
        <ItemImageArea
          size={responsive ? (!mdUp ? 'x-small' : 'medium-small') : 'x-small'}
          src={item.thumbnail.front?.smallUri ?? ''}
        />
      </Stack>
      <Stack spacing={1} sx={{ px: 1, py: '6px', minWidth: 0, width: '100%' }}>
        {item.collection?.title ? (
          <Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={styleSets.lineCamp(1)}
                flexGrow={1}
              >
                {item.collection?.title}
              </Typography>
              {item.isPremium && (
                <Icons.MembersOnly
                  fontSize="small"
                  sx={{ color: 'info.main', height: 12, width: 12, ml: 1 }}
                />
              )}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" sx={styleSets.lineCamp(1)}>
                {item.title}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="subtitle2"
                sx={styleSets.lineCamp(1)}
                flexGrow={1}
              >
                {item.title}
              </Typography>
              {item.isPremium && (
                <Icons.MembersOnly
                  fontSize="small"
                  sx={{ color: 'info.main', height: 12, width: 12, ml: 1 }}
                />
              )}
            </Stack>
          </>
        )}
        {item.price ? (
          <Stack direction="row">
            {item.paymentType === UserPaymentType.Cash ? (
              <EmphasizedNumber variant="subtitle1">
                ¥ {item.formattedPrice}
              </EmphasizedNumber>
            ) : (
              <>
                <Icons.Point fontSize="small" />
                <EmphasizedNumber variant="subtitle1">
                  {item.formattedPrice}
                </EmphasizedNumber>
              </>
            )}
          </Stack>
        ) : (
          <Typography variant="overline" color="primary">
            {t('free', '無料')}
          </Typography>
        )}
      </Stack>
    </ButtonBase>
  )
}
